import {
    Document,
    Font,
    Image,
    Page,
    StyleSheet,
    Text,
    View,
} from "@react-pdf/renderer";

import { Style } from "@react-pdf/types";

import PdfHeader from "../../../assets/print/pdfHeader.png";
import PdfFooter from "../../../assets/print/pdfFooter.png";
import Stamp from "../../../assets/print/stamp.png";

import { Table, TR, TH, TD } from "@ag-media/react-pdf-table";

import { ReactNode } from "react";
import { ToWords } from "to-words";

import CairoExtraLight from "../../../assets/fonts/cairo/static/Cairo-ExtraLight.ttf";
import CairoLight from "../../../assets/fonts/cairo/static/Cairo-Light.ttf";
import CairoRegular from "../../../assets/fonts/cairo/static/Cairo-Regular.ttf";
import CairoMedium from "../../../assets/fonts/cairo/static/Cairo-Medium.ttf";
import CairoSemiBold from "../../../assets/fonts/cairo/static/Cairo-SemiBold.ttf";
import CairoBold from "../../../assets/fonts/cairo/static/Cairo-Bold.ttf";
import CairoExtraBold from "../../../assets/fonts/cairo/static/Cairo-ExtraBold.ttf";
import CairoBlack from "../../../assets/fonts/cairo/static/Cairo-Black.ttf";
import { formatDate, normalizeArabic } from "@/v2/utils";
import { PurchaseOrder } from "@/v2/types";
import { Branches, PRINT_DEFAULT_VALUES } from "@/v2/constants";

Font.registerHyphenationCallback((word) => [word]);

Font.register({
    family: "ArabicFont",
    fonts: [
        { src: CairoExtraLight, fontWeight: 100 },
        { src: CairoExtraLight, fontWeight: 200 },
        { src: CairoLight, fontWeight: 300 },
        { src: CairoRegular, fontWeight: 400 },
        { src: CairoMedium, fontWeight: 500 },
        { src: CairoSemiBold, fontWeight: 600 },
        { src: CairoBold, fontWeight: 700 },
        { src: CairoExtraBold, fontWeight: 800 },
        { src: CairoBlack, fontWeight: 900 },
    ],
});

const color = {
    brand: "#ff9500",
    bg: "#f2f2f2",
};

const toWords = new ToWords({
    converterOptions: {
        currency: true,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false,
        currencyOptions: {
            name: "Riyal",
            plural: "Riyals",
            symbol: "SAR",
            fractionalUnit: {
                name: "Halala",
                plural: "Halalas",
                symbol: "",
            },
        },
    },
});

const styles = StyleSheet.create({
    page: {
        padding: 20,
        paddingTop: 0,
        paddingBottom: 20,
        height: "100%",
        fontSize: "8px",
    },
    HomePage: {
        padding: 20,
        paddingTop: 0,
        paddingBottom: 0,
        height: "100%",
        fontSize: "8px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
    },
    headerImage: {
        position: "absolute",
        top: 10,
        left: 20,
        right: 30,
        width: "90%",
    },
    footerImage: {
        position: "absolute",
        bottom: 0,
        left: "0",
        width: "100%",
    },
    headingStyle: {
        fontFamily: "Times-Roman",
        fontSize: 16,
        fontWeight: "semibold",
        textAlign: "center",
        letterSpacing: 5,
        padding: "10px",
    },
    addressSection: {
        position: "relative",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        flexDirection: "column",
    },
    leftAddress: {
        border: "2px solid black",
        padding: "2px",
        borderTop: "2px solid #d58f42",
        borderRight: "none",
        width: "50%",
    },
    rightAddress: {
        border: "2px solid black",
        padding: "10px",
        width: "50%",
    },
    flexCenter: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
    },
    flexBetween: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
    },
    textMiddle: {
        textAlign: "center",
        width: "60%",
    },
    arabicText: {
        textAlign: "right",
        fontFamily: "ArabicFont",
        // flexDirection: "row-reverse",
    },
});

const Header = () => (
    <View
        style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginBottom: "10px",
            paddingTop: "10px",
        }}
        fixed
    >
        <Image src={PdfHeader} style={{ width: "400px" }} />
    </View>
);

const Footer = () => (
    <View
        style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: 10,
        }}
        fixed
    >
        <Image src={PdfFooter} style={{ width: "400px" }} />
    </View>
);

// Repeated on every page of an unissued (draft) purchase order
const DraftWatermark = () => (
    <View
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}
        fixed
    >
        <Text
            style={{
                fontFamily: "Helvetica-Bold",
                fontSize: 120,
                color: "#9ca3af",
                opacity: 0.18,
                transform: "rotate(-45deg)",
            }}
        >
            DRAFT
        </Text>
    </View>
);

const NewPage = ({
    children,
    isDraft = false,
}: {
    children?: ReactNode;
    isDraft?: boolean;
}) => (
    <Page size="A4" style={styles.HomePage}>
        <View>
            <Header />
            {children}
        </View>
        <View
            style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                width: "100%",
            }}
        >
            {/* A draft isn't an official purchase order, so it isn't stamped */}
            {!isDraft && <Image src={Stamp} style={{ width: "100px" }} />}
        </View>
        <Footer />
        {/* Rendered last so it sits above the shaded boxes and tables */}
        {isDraft && <DraftWatermark />}
    </Page>
);

const QuoteHeading = ({
    englishTitle,
    arabicTitle,
}: {
    englishTitle: string;
    arabicTitle: string;
}) => (
    <View style={[styles.flexBetween, { fontWeight: 700, fontSize: "11px" }]}>
        <Text
            style={{
                color: "#ff9500",
            }}
        >
            {englishTitle}
        </Text>
        <Text></Text>
        <Text
            style={[
                {
                    color: "#ff9500",
                },
                styles.arabicText,
            ]}
        >
            {arabicTitle}
        </Text>
    </View>
);

const TableTitle = ({
    arabicTitle,
    englishTitle,
}: {
    arabicTitle: string;
    englishTitle: string;
}) => (
    <View>
        <Text style={styles.arabicText}>{arabicTitle}</Text>
        <Text>{englishTitle}</Text>
    </View>
);

const TableRowHeading = ({
    englishTitle,
    arabicTitle,
}: {
    englishTitle: string;
    arabicTitle: string;
}) => (
    <TH style={[styles.flexBetween, { fontWeight: 700, fontSize: "11px" }]}>
        <TD
            style={{
                color: "#ff9500",
            }}
        >
            {englishTitle}
        </TD>
        <TD
            style={[
                {
                    color: "#ff9500",
                    justifyContent: "flex-end",
                },
                styles.arabicText,
            ]}
        >
            {arabicTitle}
        </TD>
    </TH>
);

const TableRowContent = ({
    arabicLabel,
    englishLabel,
    content,
    isArabic = false,
    boldContent = false,
}: {
    arabicLabel: string;
    englishLabel: string;
    content: string;
    isArabic?: boolean;
    boldContent?: boolean;
}) => (
    <TR>
        <TD>{englishLabel}</TD>
        <TD
            style={{
                textAlign: "center",
                justifyContent: "center",
                ...(isArabic ? styles.arabicText : {}),
                ...(boldContent ? { fontWeight: 700 } : {}),
            }}
        >
            {content || "N/A"}
        </TD>
        <TD
            style={[
                styles.arabicText,
                {
                    justifyContent: "flex-end",
                },
            ]}
        >
            {arabicLabel}
        </TD>
    </TR>
);

const TableRowContentWithArabic = ({
    arabicLabel,
    englishLabel,
    content,
    contentInArabic,
    isHeading = false,
    style,
}: {
    arabicLabel: string;
    englishLabel: string;
    content: string;
    contentInArabic: string;
    isHeading?: boolean;
    style?: Style | Style[];
}) => (
    <TR style={{ ...style }}>
        <TD>{englishLabel}</TD>
        <TD
            style={{
                textAlign: "center",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Text
                style={{
                    ...styles.arabicText,
                    textAlign: "center",
                    ...(isHeading ? { fontWeight: 800 } : {}),
                }}
            >
                {normalizeArabic(contentInArabic) || "N/A"}
            </Text>
            <Text
                style={{
                    textAlign: "center",
                    ...(isHeading ? { fontWeight: 800 } : {}),
                }}
            >
                {content || "N/A"}
            </Text>
        </TD>

        <TD
            style={[
                styles.arabicText,
                {
                    justifyContent: "flex-end",
                },
            ]}
        >
            {arabicLabel}
        </TD>
    </TR>
);

const BankDetails = () => (
    <View
        style={{
            marginVertical: "10px",
            fontSize: "8px",
            marginTop: 0,
        }}
    >
        <Table
            style={{
                border: "0px",
            }}
            weightings={[2, 4, 2]}
            trStyle={{
                alignItems: "flex-start",
            }}
        >
            <TableRowHeading
                englishTitle="Bank Details"
                arabicTitle="ﺗﻔﺎﺻﻴﻞ اﻟﺒﻨﻚ"
            />

            <TableRowContent
                englishLabel="Account Name"
                arabicLabel="اسم الحساب"
                content="Establishment Abdul Lateef Mohammed Saeed Al Refai"
            />
            <TableRowContent
                englishLabel="Account No"
                arabicLabel="رﻗﻢ اﻟﺤﺴﺎب"
                content="35176750000108"
            />
            <TableRowContent
                englishLabel="IBAN"
                arabicLabel="اﻳﺒﺎن"
                content="SA9210000035176750000108"
            />
            <TableRowContent
                englishLabel="Swift Code / BIC"
                arabicLabel="ﺳﻮﻳﻔﺖ ﻛﻮد / ﺑﻴﻚ"
                content="NCBKSAJE"
            />
            <TableRowContent
                englishLabel="Bank Name"
                arabicLabel="اﺳﻢ اﻟﺒﻨﻚ"
                content="Saudi National Bank (SNB)"
            />
            <TableRowContent
                englishLabel="Bank Address"
                arabicLabel="ﻋﻨﻮان اﻟﺒﻨﻚ"
                content="Yanbu"
            />
        </Table>
    </View>
);

const TermsAndCondition = ({ terms }: { terms: string[] }) => (
    <View>
        <QuoteHeading
            englishTitle="Terms & Conditions"
            arabicTitle="اﻟﺸﺮوط واﻷﺣﻜﺎم"
        />
        {terms && terms.length > 0 ? (
            terms.map((term, index) => (
                <Text
                    key={index}
                    style={{
                        margin: "2px 0px",
                    }}
                >
                    {index + 1}. {term}
                </Text>
            ))
        ) : (
            <Text style={{ fontSize: 6, margin: "2px 0px" }}>
                No terms and conditions specified
            </Text>
        )}
    </View>
);

const FinalAmountDetails = ({
    englishLabel,
    amount,
    arabicLabel,
}: {
    englishLabel: string;
    amount: string | number;
    arabicLabel: string;
}) => (
    <Table style={{ border: 0 }}>
        <TR>
            <TD>{englishLabel}</TD>
            <TD style={{ justifyContent: "center" }}>{amount} SR</TD>
            <TD style={{ ...styles.arabicText, justifyContent: "flex-end" }}>
                {arabicLabel}
            </TD>
        </TR>
    </Table>
);

const GrandTotal = ({
    englishLabel,
    amount,
    arabicLabel,
}: {
    englishLabel: string;
    amount: string | number;
    arabicLabel: string;
}) => (
    <Table
        style={{
            border: 0,
            borderTop: 1,
            borderColor: color.brand,
            marginTop: "5px",
        }}
    >
        <TR style={{ marginTop: "5px" }}>
            <TD style={{ fontWeight: 800 }}>{englishLabel}</TD>
            <TD style={{ fontWeight: 800, justifyContent: "center" }}>
                {amount} SR
            </TD>
            <TD style={{ ...styles.arabicText, justifyContent: "flex-end" }}>
                {arabicLabel}
            </TD>
        </TR>
    </Table>
);

const PurchaseOrderPdf = ({
    data,
    addressBranch,
    isDraft = false,
}: {
    data: PurchaseOrder;
    addressBranch: Branches;
    isDraft?: boolean;
}) => {
    // Calculate totals
    const calculateTotals = () => {
        if (!data.items || data.items.length === 0) {
            return { totalTaxable: 0, totalTax: 0, grandTotal: 0 };
        }

        let totalTaxable = 0;
        let totalTax = 0;

        data.items.forEach((item) => {
            const unitPrice = Number(item.unit_price) || 0;
            const quantity = Number(item.quantity) || 0;
            const taxRate = Number(item.tax_rate) || 0;

            const itemTaxable = unitPrice * quantity;
            const itemTax = itemTaxable * (taxRate / 100);

            totalTaxable += itemTaxable;
            totalTax += itemTax;
        });

        const grandTotal = totalTaxable + totalTax;

        return {
            totalTaxable: totalTaxable.toFixed(2),
            totalTax: totalTax.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
        };
    };

    const { totalTaxable, totalTax, grandTotal } = calculateTotals();

    return (
        <Document>
            <NewPage isDraft={isDraft}>
                <View style={styles.flexCenter}>
                    <View
                        style={{
                            display: "flex",
                            alignItems: "center",
                            paddingBottom: "20px",
                            fontSize: "12px",
                            fontWeight: 700,
                        }}
                    >
                        <Text style={styles.arabicText}>أمر شراء</Text>
                        <Text>PURCHASE ORDER</Text>
                    </View>
                </View>

                <View
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: 10,
                    }}
                >
                    <View
                        style={{
                            display: "flex",
                            flex: "1",
                            backgroundColor: color.bg,
                            marginRight: "2px",
                            padding: "10px",
                            borderRadius: "4px",
                        }}
                    >
                        <Table
                            style={{
                                border: "0px",
                                gap: "5px",
                            }}
                            weightings={[2, 4, 2]}
                            trStyle={{
                                alignItems: "flex-start",
                            }}
                        >
                            <TableRowHeading
                                arabicTitle="lالمشتري"
                                englishTitle="Purchaser"
                            />

                            <TableRowContentWithArabic
                                arabicLabel="الاسم"
                                englishLabel="Name"
                                content="Abdul Lathif Mohammed Al Refae Est."
                                contentInArabic="مؤسسة عبداللطيف محمد سعيد الرفاعي للمقاولات"
                                isHeading={true}
                            />
                            <TableRowContentWithArabic
                                englishLabel="Address"
                                arabicLabel="العنوان"
                                content={
                                    PRINT_DEFAULT_VALUES.address[addressBranch]
                                }
                                contentInArabic={
                                    PRINT_DEFAULT_VALUES.arabicAddress[
                                        addressBranch
                                    ]
                                }
                                style={{ marginBottom: "10px" }}
                            />

                            <TableRowContent
                                arabicLabel="الرقم الضريبي"
                                englishLabel="VAT No"
                                content={PRINT_DEFAULT_VALUES.vatNumber}
                            />
                            <TableRowContent
                                arabicLabel="بريد الالكتروني"
                                englishLabel="Email"
                                content={PRINT_DEFAULT_VALUES.mail}
                            />
                            <TableRowContent
                                arabicLabel="السجل التجاري"
                                englishLabel="CR No"
                                content={
                                    PRINT_DEFAULT_VALUES.crNumber[addressBranch]
                                }
                            />
                        </Table>
                    </View>

                    <View
                        style={{
                            flex: "1",
                            backgroundColor: color.bg,
                            marginLeft: "2px",
                            padding: "10px",
                            borderRadius: "4px",
                        }}
                    >
                        <Table
                            style={{
                                border: "0px",
                                gap: "5px",
                            }}
                            weightings={[2, 4, 2]}
                            trStyle={{
                                alignItems: "flex-start",
                            }}
                        >
                            <TableRowHeading
                                arabicTitle="المورد"
                                englishTitle="Supplier"
                            />

                            <TableRowContentWithArabic
                                arabicLabel="الاسم"
                                englishLabel="Name"
                                content={data.supplier?.full_name || "N/A"}
                                contentInArabic={
                                    data.supplier?.full_name_ar || ""
                                }
                                isHeading={true}
                            />

                            <TableRowContentWithArabic
                                englishLabel="Address"
                                arabicLabel="العنوان"
                                content={data.supplier?.company_address || ""}
                                contentInArabic={
                                    data.supplier?.address_ar || ""
                                }
                                style={{ marginBottom: "10px" }}
                            />
                            <TableRowContent
                                arabicLabel="الرقم الضريبي"
                                englishLabel="VAT No"
                                content={data.supplier.vat_number || "N/A"}
                            />
                            <TableRowContent
                                arabicLabel="بريد الالكتروني"
                                englishLabel="Email"
                                content={data.supplier.email || "N/A"}
                            />
                            <TableRowContent
                                englishLabel="CR No"
                                arabicLabel="السجل التجاري"
                                content={data.supplier.cr_number || "N/A"}
                            />
                        </Table>
                    </View>
                </View>

                <View
                    style={{
                        backgroundColor: color.bg,
                        marginVertical: "4px",
                        display: "flex",
                        flexDirection: "row",
                        marginTop: 10,
                        paddingVertical: "10px",
                        borderRadius: "4px",
                    }}
                >
                    <View
                        style={{
                            flex: "1",
                            paddingHorizontal: "10px",
                        }}
                    >
                        <Table
                            style={{
                                border: "0px",
                            }}
                            weightings={[2, 4, 2]}
                            trStyle={{
                                alignItems: "flex-start",
                            }}
                        >
                            <TableRowContent
                                englishLabel="PO No."
                                arabicLabel="رقم أمر الشراء"
                                content={
                                    isDraft
                                        ? "DRAFT"
                                        : data?.purchase_order_number || "N/A"
                                }
                                boldContent={true}
                            />
                            <TableRowContent
                                englishLabel="Date"
                                arabicLabel="التاريخ"
                                content={formatDate(data.order_date) || "N/A"}
                            />
                            <TableRowContent
                                englishLabel="Created By"
                                arabicLabel="تم الإنشاء بواسطة"
                                content={data.user?.full_name || "N/A"}
                            />
                        </Table>
                    </View>

                    <View
                        style={{
                            flex: "1",
                            paddingLeft: "14px",
                            paddingRight: "10px",
                        }}
                    >
                        <Table
                            style={{
                                border: "0px",
                            }}
                            weightings={[2, 4, 2]}
                            trStyle={{
                                alignItems: "flex-start",
                            }}
                        >
                            <TableRowContent
                                englishLabel="Reference No."
                                arabicLabel="رقم المرجع"
                                content={data.reference_number || "N/A"}
                            />
                        </Table>
                    </View>
                </View>

                <View
                    style={{
                        marginVertical: "20px",
                        textAlign: "justify",
                        fontSize: "9px",
                    }}
                >
                    <View style={{ display: "flex", flexDirection: "row" }}>
                        <Text>Attention : </Text>
                        <View>
                            <Text>{data.recipient || ""}</Text>
                            <Text
                                style={{
                                    color: "#7d7d7d",
                                    fontStyle: "italic",
                                }}
                            >
                                {data.recipient_designation || ""}
                            </Text>
                        </View>
                    </View>

                    <View
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 4,
                            fontWeight: 600,
                            marginVertical: "10px",
                        }}
                    >
                        <Text>Subject : </Text>
                        <Text style={{ fontStyle: "italic" }}>
                            {data.subject || "N/A"}
                        </Text>
                    </View>

                    <View
                        style={{
                            textAlign: "justify",
                            display: "flex",
                            flexDirection: "column",
                            gap: "5px",
                        }}
                    >
                        <Text>Dear Sir/Ma'am,</Text>
                        <Text style={{ fontWeight: 200 }}>
                            With reference to your quotation, please be informed
                            that we accepted with terms & Conditions with below
                            prices as mentioned.
                        </Text>
                    </View>
                </View>
                <Table
                    style={{
                        border: "0px",
                        borderRadius: "4px",
                        overflow: "hidden",
                    }}
                    weightings={[0.125, 1, 0.5, 0.3, 0.5, 0.75, 0.7, 0.375]}
                    trStyle={{
                        backgroundColor: color.bg,
                        paddingHorizontal: "4px",
                    }}
                    tdStyle={{
                        padding: "2px",
                    }}
                >
                    <TH
                        style={{
                            backgroundColor: color.brand,
                            color: "#ffffff",
                            padding: "4px",
                        }}
                    >
                        <TD>No.</TD>
                        <TD>
                            <TableTitle
                                arabicTitle="تفاصيل السلع أو الخدمات"
                                englishTitle={`NATURE OF GOODS OR${"\n"}SERVICES`}
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="سعر الوحدة"
                                englishTitle="UNIT PRICE"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="كمية"
                                englishTitle="QUANTITY"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="وحدة قياس"
                                englishTitle="UNIT"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="المبلغ الخاضع للضريبة"
                                englishTitle="TAXABLE AMOUNT"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="الضريبة@ نسبة الضريبة"
                                englishTitle="TAX @TAX RATE"
                            />
                        </TD>
                        <TD
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TableTitle
                                arabicTitle="المجموع"
                                englishTitle="TOTAL"
                            />
                        </TD>
                    </TH>

                    {data.items && data.items.length > 0 ? (
                        data.items.map((item, index) => {
                            const unitPrice = Number(item.unit_price) || 0;
                            const quantity = Number(item.quantity) || 0;
                            const taxRate = Number(item.tax_rate) || 0;

                            const taxableAmount = unitPrice * quantity;
                            const taxAmount = taxableAmount * (taxRate / 100);
                            const totalAmount = taxableAmount + taxAmount;

                            return (
                                <TR
                                    style={{
                                        paddingVertical: "10px",
                                    }}
                                    key={index}
                                    wrap={false}
                                >
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {index + 1}
                                    </TD>

                                    <TD
                                        style={{
                                            display: "flex",
                                        }}
                                    >
                                        {item.description}
                                    </TD>

                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.unit_price}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.quantity}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {item.unit}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {taxableAmount.toFixed(2)}
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {taxAmount.toFixed(2)}@{taxRate}%
                                    </TD>
                                    <TD
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {totalAmount.toFixed(2)}
                                    </TD>
                                </TR>
                            );
                        })
                    ) : (
                        <TR>
                            <TD
                                style={{ textAlign: "center", padding: "10px" }}
                            >
                                No items available
                            </TD>
                        </TR>
                    )}
                </Table>

                <View style={{ paddingTop: "10px" }}>
                    <Text>
                        This Purchase Order is raised to order the items as
                        listed and subject to provisions the attached/listed
                        below and related to our procurement procedures. Please
                        return a copy of this Purchase Order duly signed &
                        stamped in acknowledgement of your receipt,acceptance &
                        compliance to all the terms stated herein.
                    </Text>
                </View>

                <View style={{ marginTop: "10px" }}>
                    <View
                        wrap={false}
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "20px",
                        }}
                    >
                        <View style={{ flex: "1", width: "50%" }}>
                            <BankDetails />
                        </View>

                        <View
                            wrap={false}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                width: "50%",
                            }}
                        >
                            <Table style={{ border: 0 }}>
                                <TH
                                    style={[
                                        {
                                            fontWeight: 700,
                                            fontSize: "11px",
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                        },
                                    ]}
                                >
                                    <TD
                                        style={{
                                            color: "#ff9500",
                                        }}
                                    >
                                        Financial Summary
                                    </TD>
                                    <TD
                                        style={[
                                            {
                                                color: "#ff9500",
                                                justifyContent: "flex-end",
                                            },
                                            styles.arabicText,
                                        ]}
                                    >
                                        الملخص المالي
                                    </TD>
                                </TH>
                            </Table>
                            <FinalAmountDetails
                                englishLabel="Total Taxable Amount"
                                arabicLabel="إجمالي المبلغ الخاضع للضريبة"
                                amount={totalTaxable}
                            />
                            <FinalAmountDetails
                                englishLabel="Total Tax Amount"
                                arabicLabel="إجمالي مبلغ الضريبة"
                                amount={totalTax}
                            />
                            <GrandTotal
                                englishLabel="Grand Total"
                                arabicLabel="الإجمالي الكلي"
                                amount={grandTotal}
                            />

                            <View
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    width: "100%",
                                }}
                            >
                                <View
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        marginTop: 10,
                                        width: "100%",
                                    }}
                                >
                                    <View
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Text style={{ color: "#7D7D7D" }}>
                                            Total ( in Words )
                                        </Text>
                                        <Text
                                            style={{
                                                ...styles.arabicText,
                                                color: "#7D7D7D",
                                            }}
                                        >
                                            المجموع كتابةً
                                        </Text>
                                    </View>
                                    <Text
                                        style={{
                                            marginTop: "5px",
                                            fontWeight: 800,
                                        }}
                                    >
                                        {toWords.convert(Number(grandTotal))}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <View>
                        <TermsAndCondition
                            terms={data.terms_and_conditions || []}
                        />
                    </View>
                </View>
            </NewPage>
        </Document>
    );
};

export default PurchaseOrderPdf;
